import { isTRPCClientError } from '@trpc/client';

import { RouteAuthorizationError } from '#src/utils/route-authorization.js';
import { ChunkLoadErrorDisplay } from './chunk-load-error-display.js';
import { resolveChunkLoadRecovery } from './chunk-load-recovery.js';
import { RouteAuthorizationErrorDisplay } from './route-authorization-error-display.js';
import { UnauthenticatedErrorDisplay } from './unauthenticated-error-display.js';

import type { ReactNode } from 'react';

export type ErrorBoundaryRenderProps = {
	error: unknown;
	renderUnknownError: () => ReactNode;
};

export function ErrorBoundaryRender({ error, renderUnknownError }: ErrorBoundaryRenderProps) {
	// A no-session auth error: logging in resolves it, so this branch's redirect is unguarded.
	// invalid_grant is Auth0 rejecting a stored refresh token (expired, revoked, or rotated away).
	const isAuth0NoSession =
		error instanceof Error &&
		typeof error.cause === 'object' &&
		error.cause !== null &&
		'error' in error.cause &&
		(error.cause.error === 'login_required' ||
			error.cause.error === 'missing_refresh_token' ||
			error.cause.error === 'invalid_grant');

	// A backend UNAUTHORIZED may persist after re-login (stale token, unverified, unprovisioned),
	// so its redirect is guarded against looping.
	const isBackendUnauthorized =
		isTRPCClientError(error) && (error.data as { code?: string } | null | undefined)?.code === 'UNAUTHORIZED';

	if (isAuth0NoSession || isBackendUnauthorized) {
		return <UnauthenticatedErrorDisplay guarded={isBackendUnauthorized} />;
	}

	if (error instanceof RouteAuthorizationError) {
		return <RouteAuthorizationErrorDisplay />;
	}

	const chunkLoadRecovery = resolveChunkLoadRecovery(error);
	if (chunkLoadRecovery !== undefined) {
		return <ChunkLoadErrorDisplay outcome={chunkLoadRecovery} error={error} inBoundary />;
	}

	return renderUnknownError();
}
